'use client'

import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from '@/hooks/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AlertTriangle,
  Check,
  Copy,
  History,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  PhoneCall,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react'
import {
  StatCard,
  addStaffSchema,
  editStaffSchema,
  useCalls,
  type AddStaffFormValues,
  type EditStaffFormValues,
} from '@/features/cases'
import {
  ALL_FILTER_VALUE,
  countBy,
  TableFilterTabs,
  TableSearch,
} from '@/components/patterns/table-filter-tabs'
import { Pagination } from '@/components/patterns/pagination'
import { SyncStatusLine } from '@/features/admin/components/syncStatusLine'
import { UserAuditLogDialog } from '@/features/admin/components/userAuditLogDialog'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AdminShell } from '@/features/admin/components/admin-shell'
import {
  useAdminUsers,
  useCreateStaff,
  useDeactivateStaff,
  useResetStaffPassword,
  useSendLoginDetails,
  useUpdateStaff,
} from '@/features/admin/hooks/use-admin-users'
import { useAdminOverview } from '@/features/admin/hooks/use-admin-overview'
import type { AdminUserSummary } from '@/features/admin/api/admin-users.api'
import { AppError } from '@/lib/errors/appError'

const ADD_STAFF_DEFAULTS: AddStaffFormValues = { name: '', email: '', phone: '' }

/**
 * Creates a real staff account: a new Clerk user (name/email/auto-generated password, role
 * fixed server-side to `staff`) plus a local `User` row, immediately reflected in the list
 * (see `useCreateStaff`). The phone number is validated and stored in this app's own database
 * only — never sent to Clerk, which rejects Indian phone numbers outright.
 */
function AddStaffDialog({
  onCreated,
}: {
  onCreated: (result: { name: string; email: string; password: string; emailSent: boolean }) => void
}) {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createStaff = useCreateStaff()
  const form = useForm<AddStaffFormValues>({
    resolver: zodResolver(addStaffSchema),
    mode: 'onTouched',
    defaultValues: ADD_STAFF_DEFAULTS,
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null)
    try {
      const result = await createStaff.mutateAsync(values)
      setOpen(false)
      form.reset(ADD_STAFF_DEFAULTS)
      onCreated({
        name: values.name,
        email: values.email,
        password: result.password,
        emailSent: result.emailSent,
      })
    } catch (err) {
      const appError = AppError.fromUnknown(err)
      setError(
        appError.code === 'RESOURCE_CONFLICT'
          ? appError.message
          : 'Something went wrong. Please try again.',
      )
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          form.reset(ADD_STAFF_DEFAULTS)
          setError(null)
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="size-4" /> Add Staff
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Add a staff member</DialogTitle>
            <DialogDescription>
              They&apos;ll sign in on the Staff tab at /team/login with this email.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="staff-name">Full name</FieldLabel>
              <Input id="staff-name" placeholder="e.g. Kavya Menon" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="staff-email">Work email</FieldLabel>
              <Input
                id="staff-email"
                type="email"
                placeholder="kavya.menon@nivex.in"
                {...form.register('email')}
              />
              <FieldError errors={[form.formState.errors.email]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="staff-phone">Mobile number</FieldLabel>
              <div className="flex gap-2">
                <span className="bg-muted text-muted-foreground flex h-9 shrink-0 items-center rounded-md border px-3 text-sm font-medium">
                  🇮🇳 +91
                </span>
                <Input
                  id="staff-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter 10-digit number"
                  {...form.register('phone', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    },
                  })}
                />
              </div>
              <FieldError errors={[form.formState.errors.phone]} />
            </Field>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createStaff.isPending}>
              {createStaff.isPending ? 'Adding…' : 'Add staff member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Updates an existing staff member's name/email/phone. Name always updates on Clerk; email
 * only round-trips to Clerk (create new primary, delete old) when it actually changed — see
 * `updateStaff` on the backend. The phone number stays DB-only, same as `AddStaffDialog`.
 */
function EditStaffDialog({
  user,
  open,
  onOpenChange,
}: {
  user: AdminUserSummary | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const updateStaff = useUpdateStaff()
  const form = useForm<EditStaffFormValues>({
    resolver: zodResolver(editStaffSchema),
    mode: 'onTouched',
    values: {
      name: user?.name ?? '',
      email: user?.email ?? '',
      phone: user?.phone ?? '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    if (!user) return
    setError(null)
    try {
      await updateStaff.mutateAsync({ userId: user.id, input: values })
      onOpenChange(false)
    } catch (err) {
      const appError = AppError.fromUnknown(err)
      setError(
        appError.code === 'RESOURCE_CONFLICT'
          ? appError.message
          : 'Something went wrong. Please try again.',
      )
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) setError(null)
      }}
    >
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Edit staff member</DialogTitle>
            <DialogDescription>
              Updates take effect immediately across every console.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="edit-staff-name">Full name</FieldLabel>
              <Input id="edit-staff-name" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-staff-email">Work email</FieldLabel>
              <Input id="edit-staff-email" type="email" {...form.register('email')} />
              <FieldError errors={[form.formState.errors.email]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-staff-phone">Mobile number</FieldLabel>
              <div className="flex gap-2">
                <span className="bg-muted text-muted-foreground flex h-9 shrink-0 items-center rounded-md border px-3 text-sm font-medium">
                  🇮🇳 +91
                </span>
                <Input
                  id="edit-staff-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="Enter 10-digit number"
                  {...form.register('phone', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    },
                  })}
                />
              </div>
              <FieldError errors={[form.formState.errors.phone]} />
            </Field>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={updateStaff.isPending}>
              {updateStaff.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Shared by the "Reset password", "Add staff", and "Send login details" flows — same shape
 * either way (a name and a one-time password to copy), just different copy since a reset/
 * send-login-details also signs out every other session and a fresh account has no prior
 * session to sign out of. `created`/`login-details` also carry `emailSent`, since those two
 * flows attempt an automatic email — `reset` never does, so it has no status line at all.
 */
function PasswordResultDialog({
  result,
  onOpenChange,
}: {
  result: {
    name: string
    email: string
    password: string
    mode: 'reset' | 'created' | 'login-details'
    emailSent?: boolean
  } | null
  onOpenChange: (open: boolean) => void
}) {
  const [copied, setCopied] = useState(false)

  const copyPassword = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.password)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog
      open={!!result}
      onOpenChange={(open) => {
        if (!open) setCopied(false)
        onOpenChange(open)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {result?.mode === 'created' ? 'Password for' : 'New password for'} {result?.name}
          </DialogTitle>
          <DialogDescription>
            {result?.mode === 'created'
              ? "Share this with them securely — it's shown once and isn't stored anywhere."
              : "Share this with them securely — it's shown once and isn't stored anywhere. " +
                "They've been signed out of any active session."}
          </DialogDescription>
        </DialogHeader>
        {result &&
          result.mode !== 'reset' &&
          (result.emailSent ? (
            <p className="text-sm text-green-600 dark:text-green-500">
              Login credentials sent to {result.email}.
            </p>
          ) : (
            <p className="text-destructive text-sm">
              Email couldn&apos;t be sent due to an error — please share this password with them
              directly.
            </p>
          ))}
        <div className="flex items-center gap-2 py-2">
          <Input readOnly value={result?.password ?? ''} className="font-mono" />
          <Button type="button" variant="outline" size="icon" onClick={copyPassword}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const ROLE_ORDER = ['staff', 'admin'] as const
const ROLE_LABEL: Record<(typeof ROLE_ORDER)[number], string> = { staff: 'Staff', admin: 'Admin' }

type RoleFilter = typeof ALL_FILTER_VALUE | (typeof ROLE_ORDER)[number]

const PAGE_SIZE = 20

export function AdminStaffView() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const { data: usersPage, isLoading } = useAdminUsers({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
  })
  const { data: overview } = useAdminOverview()
  // The smallest legal page fetch (page 1, pageSize 1), read only for its scope-wide `total` —
  // a deliberate reuse of the endpoint the Calls screens need anyway, not a new backend call.
  const { data: callsPage } = useCalls({ page: 1, pageSize: 1 })
  const resetPassword = useResetStaffPassword()
  const sendLoginDetails = useSendLoginDetails()
  const deactivateStaff = useDeactivateStaff()

  const staff = useMemo(() => usersPage?.items ?? [], [usersPage])
  const total = usersPage?.total ?? 0
  const totalPages = usersPage?.totalPages ?? 1

  const [roleFilter, setRoleFilter] = useState<RoleFilter>(ALL_FILTER_VALUE)
  const [editing, setEditing] = useState<AdminUserSummary | null>(null)
  const [viewingActivity, setViewingActivity] = useState<AdminUserSummary | null>(null)
  const [removing, setRemoving] = useState<AdminUserSummary | null>(null)
  const [resetting, setResetting] = useState<AdminUserSummary | null>(null)
  const [sendingLoginDetails, setSendingLoginDetails] = useState<AdminUserSummary | null>(null)
  const [passwordResult, setPasswordResult] = useState<{
    name: string
    email: string
    password: string
    mode: 'reset' | 'created' | 'login-details'
    emailSent?: boolean
  } | null>(null)

  const roleOptions = useMemo(
    () =>
      countBy(
        staff,
        (s) => (s.role === 'staff' || s.role === 'admin' ? s.role : null),
        ROLE_ORDER,
        (r) => ROLE_LABEL[r],
      ),
    [staff],
  )

  // `search` is server-side now (via `useAdminUsers`'s `search` param) — this only narrows the
  // already-fetched, already-search-filtered page by role, same as before.
  const filteredStaff = useMemo(
    () => (roleFilter === ALL_FILTER_VALUE ? staff : staff.filter((s) => s.role === roleFilter)),
    [staff, roleFilter],
  )

  const unassignedCases = overview?.unassignedCount ?? 0

  const confirmReset = async () => {
    if (!resetting) return
    const target = resetting
    const result = await resetPassword.mutateAsync(target.id)
    setResetting(null)
    setPasswordResult({
      name: target.name ?? target.email,
      email: target.email,
      password: result.password,
      mode: 'reset',
    })
  }

  const confirmSendLoginDetails = async () => {
    if (!sendingLoginDetails) return
    const target = sendingLoginDetails
    const result = await sendLoginDetails.mutateAsync(target.id)
    setSendingLoginDetails(null)
    setPasswordResult({
      name: target.name ?? target.email,
      email: target.email,
      password: result.password,
      mode: 'login-details',
      emailSent: result.emailSent,
    })
  }

  const confirmRemove = async () => {
    if (!removing) return
    const target = removing
    const result = await deactivateStaff.mutateAsync(target.id)
    setRemoving(null)
    const name = target.name ?? target.email
    toast({
      variant: 'success',
      title:
        result.reassignedCaseCount > 0
          ? `${name} deactivated — ${result.reassignedCaseCount} case(s) moved to Unassigned`
          : `${name} deactivated`,
    })
  }

  return (
    <AdminShell
      active="staff"
      title="Staff"
      description="Every advisor working the case queue."
      actions={
        <div className="flex flex-col items-end gap-1">
          <AddStaffDialog
            onCreated={(result) => setPasswordResult({ ...result, mode: 'created' })}
          />
          <SyncStatusLine />
        </div>
      }
    >
      <div className="@xl/main:grid-cols-3 grid grid-cols-1 gap-4">
        <StatCard
          label="Staff"
          value={total}
          footnote="Active advisors"
          icon={<Users />}
          tint="var(--chart-1)"
        />
        <StatCard
          label="Total Calls"
          value={callsPage?.total ?? 0}
          footnote="Logged across the team"
          icon={<PhoneCall />}
          tint="var(--chart-5)"
        />
        <StatCard
          label="Unassigned Cases"
          value={unassignedCases}
          trend={unassignedCases > 0 ? 'Needs an owner' : undefined}
          trendUp={false}
          footnote="Waiting for a caller to claim them"
          icon={<AlertTriangle />}
          tint="var(--chart-4)"
        />
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="@lg/main:flex-row @lg/main:items-center @lg/main:justify-between flex flex-col gap-3">
            <TableFilterTabs
              value={roleFilter}
              onValueChange={setRoleFilter}
              allCount={staff.length}
              options={roleOptions}
              className="@lg/main:flex-1"
            />
            {/* `ml-auto`, not just relying on the row's `justify-between` — `TableFilterTabs`
                renders nothing when fewer than 2 role options are present (its own internal
                guard), which collapses this row to a single child and left-aligns it under
                `justify-between`. `ml-auto` keeps this pinned to the right whether or not the
                tabs are actually rendered. */}
            <div className="@lg/main:ml-auto">
              <TableSearch
                value={search}
                onChange={(v) => {
                  setSearch(v)
                  setPage(1)
                }}
                placeholder="Search name or email…"
                count={{ shown: filteredStaff.length, total, noun: 'staff' }}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Assigned Cases</TableHead>
                  <TableHead className="text-right">Calls Logged</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStaff.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium">{s.name ?? '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{s.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {s.role ?? 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{s.caseCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.callCount}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground size-7"
                              aria-label={`Edit ${s.name ?? s.email}`}
                              onClick={() => setEditing(s)}
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit staff member</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground size-7"
                              aria-label={`View activity for ${s.name ?? s.email}`}
                              onClick={() => setViewingActivity(s)}
                            >
                              <History className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>View activity</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-destructive hover:text-destructive size-7"
                              aria-label={`Remove ${s.name ?? s.email}`}
                              onClick={() => setRemoving(s)}
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Remove staff member</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground size-7"
                              aria-label={`Reset password for ${s.name ?? s.email}`}
                              onClick={() => setResetting(s)}
                            >
                              <KeyRound className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reset password</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-muted-foreground size-7"
                              aria-label={`Send login details to ${s.name ?? s.email}`}
                              onClick={() => setSendingLoginDetails(s)}
                            >
                              <Mail className="size-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Send login details</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      <Loader2 className="text-muted-foreground mx-auto size-6 animate-spin" />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && filteredStaff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                      {staff.length === 0 ? 'No staff yet.' : 'No staff match this filter.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <UserAuditLogDialog
        key={viewingActivity?.id ?? 'none-activity'}
        user={viewingActivity}
        open={!!viewingActivity}
        onOpenChange={(open) => !open && setViewingActivity(null)}
      />

      <EditStaffDialog
        key={editing?.id ?? 'none'}
        user={editing}
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
      />

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing?.name ?? removing?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing && removing.caseCount > 0
                ? `Their ${removing.caseCount} assigned case(s) will move back to Unassigned.`
                : 'This staff member will no longer be able to sign in.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deactivateStaff.isPending}
              onClick={() => void confirmRemove()}
            >
              {deactivateStaff.isPending ? 'Removing…' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetting} onOpenChange={(open) => !open && setResetting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Reset password for {resetting?.name ?? resetting?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll be signed out of any active session and given a brand-new password. Share
              it with them yourself once it&apos;s generated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={resetPassword.isPending}
              onClick={() => void confirmReset()}
            >
              {resetPassword.isPending ? 'Resetting…' : 'Reset password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!sendingLoginDetails}
        onOpenChange={(open) => !open && setSendingLoginDetails(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send login details to {sendingLoginDetails?.name ?? sendingLoginDetails?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This generates a brand-new password and emails it to them — any password they already
              have (including one from an earlier email) stops working immediately. Use this if the
              automatic email never arrived.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={sendLoginDetails.isPending}
              onClick={() => void confirmSendLoginDetails()}
            >
              {sendLoginDetails.isPending ? 'Sending…' : 'Send login details'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PasswordResultDialog
        result={passwordResult}
        onOpenChange={(open) => !open && setPasswordResult(null)}
      />
    </AdminShell>
  )
}
